import { Component } from 'react'
import { router } from '@inertiajs/react'
import { Center, Stack, Text, Button } from '@mantine/core'

// React error boundary (class component — React 19 still has no functional
// equivalent, and react-error-boundary isn't a dependency). Catches render-time
// throws in its subtree and shows a fallback instead of unmounting the whole
// tree to a blank page. Note: boundaries do NOT catch errors during the server
// renderToString pass — inertia_rails handles those (see config/initializers/
// inertia_rails.rb) — so this is a client/hydration safety net.
//
// The fallback text is intentionally hardcoded English rather than routed
// through the i18n catalog: this is rare, last-resort UI, and keeping it free of
// app context/catalog state makes it robust even when something upstream is what
// threw. Non-English users seeing English here is the same behavior the catalog
// fallback chain would produce, and it replaces a blank screen either way.
function PageFallback() {
  return (
    <Center mih="60vh" p="xl">
      <Stack align="center" gap="sm" maw={420} ta="center">
        <Text fw={700} size="lg">
          Something went wrong
        </Text>
        <Text c="dimmed" size="sm">
          This page hit an unexpected error. Reloading usually fixes it.
        </Text>
        <Button mt="xs" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Stack>
    </Center>
  )
}

export default class ErrorBoundary extends Component {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidMount() {
    // The layout (and thus this boundary) is persistent across Inertia
    // navigations, so a caught error would otherwise stick after navigating
    // away. Reset on navigation — without keying/remounting the page subtree,
    // which would needlessly tear down and rebuild maps on same-component
    // (e.g. station-to-station) navigations. Client-only; no-op during SSR.
    this.unlisten = router.on('navigate', () => {
      if (this.state.hasError) this.setState({ hasError: false })
    })
  }

  componentWillUnmount() {
    this.unlisten?.()
  }

  componentDidCatch(error, info) {
    // THE COMPONENT STACK GOES TO THE CONSOLE, AND ONLY THERE. Plausible takes
    // an enum, so the telemetry below can say a TypeError happened and roughly
    // where the map was — never which component threw. When a crash needs that,
    // it has to be reproduced with a console open; this line is the whole
    // record. (It is what identified Randy's 2026-09-11 desktop crash as an
    // HMR-stale layer instance rather than the phone crash it resembled.)
    console.error('[ErrorBoundary]', error, info?.componentStack)
    // The reporter this comment promised. On a phone the console.error above is
    // invisible, which is how a map that died after minutes of panning reached
    // us as a screenshot and nothing else (Randy, 2026-09-11).
    //
    // Wrapped, because a boundary that throws while reporting an error takes
    // the whole page down — the exact failure it exists to prevent, arriving
    // through the telemetry.
    try {
      this.props.onError?.(error, info)
    } catch {
      // Deliberately silent: there is nowhere left to report a reporting error.
    }
  }

  // Re-mount the subtree that threw. For a dead WebGL context this is the real
  // repair, not a lesser one — the new mount builds a new canvas and a new
  // context — and it keeps the rest of the page, which a reload discards.
  retry = () => {
    if (this.state.hasError) this.setState({ hasError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    // `fallback` may be any node (including null to render nothing), or a
    // function of `{ retry }` for a fallback that offers a way back.
    const { fallback } = this.props
    if (typeof fallback === 'function') return fallback({ retry: this.retry })
    return fallback !== undefined ? fallback : <PageFallback />
  }
}
